<template>
  <div>
    <CompaniesAnalysisLayout
      :not-available-type="analysisAxesLoading ? null : notAvailableType"
      :company-uuid="$route.params.company_uuid"
      :is-more-than-editor="showMoreThanEditorAuthority"
    >
      <!-- ヘッダー -->
      <template #header>
        <CompaniesAnalysisHeader
          :current-date="date"
          :period="period"
          :visible-export-csv="canExportAuthority('analysis/visitors') && providedFeaturesCsvExport"
          :disabled-export-csv="aggsLoading"
          @change-date="changeDate"
          @change-period="changePeriod"
          @export-csv="setExportCsv"
        >
          <template #select>
            <Select
              :model-value="dataType"
              :options="dataTypeOptions"
              @update:model-value="changeDataType"
            />
          </template>
        </CompaniesAnalysisHeader>
      </template>

      <!-- グラフ領域 -->
      <template #graph>
        <div class="p-lineChart">
          <Loading v-if="loading || analysisAxesLoading" />
          <ChartLine
            v-else
            class="p-lineChart__container"
            :chart-data="chartData"
            :options="chartOptions"
            :height="360"
          />
        </div>
      </template>

      <!-- クロス分析 -->
      <template #terms>
        <CompaniesAnalysisCrossSearch
          v-if="!analysisAxesLoading"
          :disabled="loading"
          :cross-search-field="crossSearchField"
          :current-page-type="pageType"
          :page-types="pageTypes"
          @set-page-type="changePageType"
          @set-cross-search-field="changeCrossSearchField"
        />
      </template>

      <!-- 現在の表示リスト / 表示リストのリセット -->
      <template #reset>
        <ul v-show="individualAnalysisName" class="c-links">
          <li class="c-links__item">
            <strong @click="changeIndividualAnalysisField(null)">{{ $t('pages.companies.analysis.individual.reset') }}</strong> | {{ $t('pages.companies.analysis.individual.listTitle', { name: individualAnalysisName }) }}
          </li>
        </ul>
      </template>

      <!-- テーブル -->
      <template #table>
        <CompaniesAnalysisTable
          :loading="aggsLoading || analysisAxesLoading"
          :items="aggs"
          :static-heads="dataTypes"
          :cross-search-fields="crossSearchFields"
          :search-items="displayGraphs"
          @change-display-graphs="changeDisplayGraphs"
          @change-individual-analysis-field="changeIndividualAnalysisField"
        />
      </template>
    </CompaniesAnalysisLayout>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout'
import CompaniesAnalysisHeader from '../../../components/CompaniesAnalysis/Header'
import CompaniesAnalysisCrossSearch from '../../../components/CompaniesAnalysis/CrossSearch'
import CompaniesAnalysisTable from '../../../components/CompaniesAnalysis/Table'
import Loading from '../../../components/Loading'
import Select from '../../../components/Select'
import ChartLine from '../../../components/ChartLine'
import companiesNotAvailableTypeMixin from '../../../mixins/companiesNotAvailableTypeMixin'
import { colors } from '../../../modules/analysis'
import { toNumberLocaleString } from '../../../modules/localeString'
import { i18n } from '../../../plugins/i18n'
import { generateGraphId } from '../../../modules/analysis'

export default {
  name: 'CompaniesAnalysisVisitors',
  components: {
    CompaniesAnalysisLayout,
    CompaniesAnalysisHeader,
    CompaniesAnalysisCrossSearch,
    CompaniesAnalysisTable,
    Loading,
    Select,
    ChartLine,
  },
  mixins: [companiesNotAvailableTypeMixin],
  beforeRouteUpdate(to, from, next) {
    if (this.individualAnalysisName) {
      this.setIndividualAnalysisField(null)
      this.setDisplayGraphs([generateGraphId(this.aggs[0])])
    }
    from.query.cross = this.crossSearchField?.fields
    this.initParams(to.query.type, to.query.fields)
    to.query.cross = this.crossSearchField?.fields
    if (!this.notAvailableType) {
      this.fetchData(to.query.fields !== from.query.fields || from.query.cross !== to.query.cross)
    }
    next()
  },
  computed: {
    ...mapState({
      plots: state => state.companiesAnalysisVisitors.plots,
      analysisAxesLoading: state => state.companiesAnalysisAxes.loading,
      aggsLoading: state => state.companiesAnalysisVisitors.aggsLoading,
      plotsLoading: state => state.companiesAnalysisVisitors.plotsLoading,
      dataType: state => state.companiesAnalysisVisitors.dataType,
      date: state => state.companiesAnalysisVisitors.date,
      displayGraphs: state => state.companiesAnalysisVisitors.displayGraphs,
      graphKey: state => state.companiesAnalysisVisitors.graphKey,
      pageType: state => state.companiesAnalysisVisitors.pageType,
      crossSearchField: state => state.companiesAnalysisVisitors.crossSearchField,
      period: state => state.filters.period
    }),
    ...mapGetters({
      params: 'companiesAnalysisVisitors/params',
      exportParams: 'companiesAnalysisVisitors/exportParams',
      crossSearchFields: 'companiesAnalysisVisitors/crossSearchFields',
      dataTypes: 'companiesAnalysisVisitors/dataTypes',
      pageTypes: 'companiesAnalysisVisitors/pageTypes',
      individualAnalysisName: 'companiesAnalysisVisitors/individualAnalysisName',
      aggs: 'companiesAnalysisVisitors/aggs',
      selectedAggIds: 'companiesAnalysisVisitors/selectedAggIds',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
    }),
    /**
     * データ種別の一覧
     * @return { Boolean }
     */
    dataTypeOptions() {
      const options = {}
      this.dataTypes.forEach(vt => options[vt.name] = vt.dataType)
      return options
    },
    /**
     * テーブルまたはグラフデータ取得中かどうか
     * @return { Boolean }
     */
    loading() {
      return this.aggsLoading || this.plotsLoading
    },
    /**
     * テーブルまたはグラフデータ取得 API に渡すパラメータ
     * @return { Object }
     */
    requestParams() {
      return {
        companyUuid: this.$route.params.company_uuid,
        params: {
          ...this.params,
          posted_at_term: `${ moment(this.period.startDate).format(i18n.t('format.date.default')) } - ${ moment(this.period.endDate).format(i18n.t('format.date.default')) }`
        }
      }
    },
    /**
     * @return { Object }
     */
    requestExportParams() {
      return {
        companyUuid: this.$route.params.company_uuid,
        params: {
          ...this.exportParams,
          posted_at_term: `${ moment(this.period.startDate).format(i18n.t('format.date.default')) } - ${ moment(this.period.endDate).format(i18n.t('format.date.default')) }`
        }
      }
    },
    /**
     * グラフライブラリに渡すデータ
     * @return { Object }
     */
    chartData() {
      const datasets = []
      for (const key in this.plots) {
        if (!this.selectedAggIds.includes(key)) continue
        const plot = this.plots[key]
        if (plot) {
          const data = Object.values(this.plots[key].attributes.values)
          const fields = JSON.parse(JSON.stringify(plot.attributes.fields))
          datasets.push({
            label: fields.map((field) => field.name).join(' / '),
            data,
            backgroundColor: [ 'rgba(255, 255, 255, 0)' ],
            borderColor: [ colors[datasets.length % 10] ],
            borderWidth: 2,
            pointBorderColor: colors[datasets.length % 10],
            lineTension: 0,
            pointRadius: 4
          })
        }
      }
      return { labels: this.graphKey, datasets }
    },
    /**
     * グラフライブラリに渡すオプション
     * @return { Object }
     */
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 4,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback(label) {
                if (Math.floor(label) === label) return toNumberLocaleString(label)
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            }
          }
        }
      }
    }
  },
  async created() {
    await this.readCompaniesAnalysisAxes()
    this.initParams(this.$route.query.type, this.$route.query.fields)
    if (this.notAvailableType) return
    this.fetchData(true)
  },
  methods: {
    ...mapActions({
      setDataType: 'companiesAnalysisVisitors/setDataType',
      setDate: 'companiesAnalysisVisitors/setDate',
      setDisplayGraphs: 'companiesAnalysisVisitors/setDisplayGraphs',
      setPageType: 'companiesAnalysisVisitors/setPageType',
      setCrossSearchField: 'companiesAnalysisVisitors/setCrossSearchField',
      setIndividualAnalysisField: 'companiesAnalysisVisitors/setIndividualAnalysisField',
      readAggs: 'companiesAnalysisVisitors/readAggs',
      readPlots: 'companiesAnalysisVisitors/readPlots',
      exportCsv: 'companiesAnalysisVisitors/exportCsv',
      setPeriod: 'filters/setPeriod',
      readCompaniesAnalysisAxes: 'companiesAnalysisAxes/readCompaniesAnalysisAxes'
    }),
    async fetchData(fetchAggs) {
      if (fetchAggs) {
        await this.readAggs(this.requestParams)
      }
      if (this.aggs.length) {
        this.readPlots(this.requestParams)
      }
    },
    initParams(type, fields) {
      if (this.notAvailableType) this.setCrossSearchField(null)
      this.setIndividualAnalysisField(null)
      this.setDataType(type || 'lp_pvs')
      this.setPageType(fields || 'store_id')
    },
    /**
     * データ種別の変更
     * @param { Object } value
     * @return { Void }
     */
    changeDataType(value) {
      if (this.loading) return
      this.$router.push({ query: { ...this.$route.query, type: value } })
    },
    /**
     * 日, 週, 月の変更
     * @param  { String } value
     * @return { Void }
     */
    changeDate(value) {
      if (this.loading) return
      this.setDate(value)
      this.fetchData(false)
    },
    /**
     * 期間の変更
     * @param { Object } value
     * @return { Void }
     */
    async changePeriod(value) {
      if (this.loading) return
      this.setPeriod(value)
      this.fetchData(true)
    },
    /**
     * グラフ表示対象の変更
     * @param  { String } value
     * @return { Void }
     */
    changeDisplayGraphs(value) {
      if (this.loading) return
      this.setDisplayGraphs(value)
      this.fetchData(false)
    },
    /**
     * 分析軸の変更
     * @param  { String } value
     * @return { Void }
     */
    async changePageType(value) {
      if (this.loading) return
      this.$router.push({ query: { ...this.$route.query, fields: value.fields } })
    },
    /**
     * クロス分析の変更
     * @param  { String } value
     * @return { Void }
     */
    async changeCrossSearchField(value) {
      if (this.loading) return
      this.setCrossSearchField(value)
      this.fetchData(true)
    },
    /**
     * テーブル表示リストの変更
     * @param  { String } value
     * @return { Void }
     */
    changeIndividualAnalysisField(value) {
      if (this.loading) return
      this.setIndividualAnalysisField(value)
      this.setDisplayGraphs([generateGraphId(this.aggs[0])])
      this.fetchData(false)
    },
    /**
     * @return { Void }
     */
    async setExportCsv() {
      const isSuccess = await this.exportCsv(this.requestExportParams)
      if (!isSuccess) return
      this.$router.push({ name: 'CompaniesExportResults' })
    }
  }
}
</script>
